import { useLocation } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import useAxios from "../../hooks/useAxios/index";
import DetailsTable from "../../components/DetailsTable/DetailsTable";
import ExcelExportButton from "../../components/ExcelExportButton/ExcelExportButton";
import Headline from "../../components/Headline/Headline";
import Loading from "../../components/Loading/Loading";
import Error from "../../components/Error/Error";
import SearchBar from "../../components/SearchBar/SearchBar";
import "./DetailView.css";
import { useService } from "../../context/ServiceProvider";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from '../../config/authConfig';

interface LocationState {
  type: string;
  label: string;
  apiUrl: string;
}

const DetailView = () => {
  const { state } = useLocation();
  const { type, label, apiUrl } = (state as LocationState) || {};
  const { currentService } = useService();

  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");

  const [token, setToken] = useState<string | null>(null);
  const { instance, accounts } = useMsal();

  const { response, error, loading, fetchData } = useAxios(currentService?.apiBaseUrl || "", token ?? undefined);


  useEffect(() => {
    const getToken = async () => {
      if (accounts.length === 0) return;

      try {
        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0],
        });
        setToken(result.accessToken);
      } catch (err) {
        console.error("Failed to acquire token silently:", err);
      }
    };

    getToken();
  }, [accounts, instance]);

  useEffect(() => {
    if (!token) return;
    if (apiUrl) {
      fetchData({ url: apiUrl, method: "GET" });
    }

  }, [apiUrl, token, fetchData]);

  const filteredData = useMemo(() => {
    if (!Array.isArray(response) || !searchFilter) return response;

    const lowerSearch = searchFilter.toLowerCase();

    return response.filter((item) => {
      return (currentService?.detailsTableConfig ?? []).some((col) => {
        let value: string;

        if (col.render) {
          const rendered = col.render(item);
          value = typeof rendered === "string" ? rendered : String(rendered);
        } else {
          value = (item as any)[col.accessor];
        }

        return value?.toLowerCase().includes(lowerSearch);
      });
    });
  }, [response, searchFilter, currentService]);


  if (loading) return <Loading label={label} />;

  return (
    <div className="detail-view-container">
      <div className="header-row">
        <Link to={`/${currentService?.id}/dashboard`} className="back-link">
          ← Back to Dashboard
        </Link>
        <Headline title={`Details`} subtitle={`filtered by '${type}/${label}'`} />
      </div>

      {!loading && !response && <p>No data found.</p>}
      {!loading && response && Array.isArray(response) && (
        <div className="detail-section">
          <div className="table-toolbar">
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              onSearch={() => {
                setSearchFilter(searchInput);
              }}
            />

            <div className="total-count-badge">
              {filteredData?.length ?? 0} entries
            </div>

            <ExcelExportButton
              data={filteredData}
              filename={`${currentService?.id}-[${label}${searchInput !== '' ? `-${searchInput}` : ''}]-${new Date().toISOString().slice(0, 10)}.xlsx`}
              sheetName={currentService?.id ?? ""}
            />
          </div>

          <DetailsTable data={filteredData} columns={currentService?.detailsTableConfig ?? []} />
        </div>
      )}
    </div>
  );
};

export default DetailView;
